<template>
  <v-card class="pa-5" elevation="1">
    <v-card-title class="px-0 pt-0 d-flex align-center flex-wrap">
      Projeção anual — {{ projection.current_year }}
      <v-spacer />
      <v-select
        v-model="localCompareYear"
        :items="compareYearItems"
        item-text="text"
        item-value="value"
        label="Comparar com"
        dense
        outlined
        hide-details
        class="dashboard-compare-select mt-2 mt-sm-0"
        @change="$emit('change-compare-year', localCompareYear)"
      />
    </v-card-title>

    <p class="text-caption text--secondary mb-4">
      Projeção calculada pela média mensal do ano atual ({{ projection.months_elapsed }} meses).
    </p>

    <Row class="mb-4">
      <Col cols="12" sm="6" md="3">
        <div class="projection-metric">
          <div class="text-caption text--secondary">Realizado (YTD)</div>
          <div class="text-h6">{{ formatCurrency(projection.ytd.paid_amount) }}</div>
          <div class="text-caption">{{ projection.ytd.paid_count }} pagamentos</div>
        </div>
      </Col>
      <Col cols="12" sm="6" md="3">
        <div class="projection-metric">
          <div class="text-caption text--secondary">Projeção {{ projection.current_year }}</div>
          <div class="text-h6">{{ formatCurrency(projection.projected.paid_amount) }}</div>
          <div class="text-caption">{{ projection.projected.paid_count }} pagamentos</div>
        </div>
      </Col>
      <Col cols="12" sm="6" md="3">
        <div class="projection-metric">
          <div class="text-caption text--secondary">Total {{ projection.compare_year }}</div>
          <div class="text-h6">{{ formatCurrency(projection.compare_total.paid_amount) }}</div>
          <div class="text-caption">
            YTD: {{ formatCurrency(projection.compare_ytd.paid_amount) }}
          </div>
        </div>
      </Col>
      <Col cols="12" sm="6" md="3">
        <div class="projection-metric">
          <div class="text-caption text--secondary">Variação projetada</div>
          <div
            class="text-h6"
            :class="variationClass(projection.variation_projected_percent)"
          >
            {{ formatVariation(projection.variation_projected_percent) }}
          </div>
          <div class="text-caption">
            YTD: {{ formatVariation(projection.variation_ytd_percent) }}
          </div>
        </div>
      </Col>
    </Row>

    <apexchart
      type="bar"
      height="340"
      :options="chartOptions"
      :series="chartSeries"
    />
  </v-card>
</template>

<script>
import { Row, Col } from '@/components/vuetify';
import { formatCurrency } from '@/utils';

export default {
  name: 'AdminDashboardProjection',
  components: { Row, Col },
  props: {
    projection: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localCompareYear: this.projection.compare_year,
    };
  },
  computed: {
    compareYearItems() {
      return (this.projection.available_compare_years || []).map((year) => ({
        text: String(year),
        value: year,
      }));
    },
    chartSeries() {
      const currentYear = this.projection.current_year;
      const compareYear = this.projection.compare_year;

      return [
        {
          name: `${currentYear} (realizado)`,
          data: (this.projection.by_month || []).map((item) => (
            item.projected_amount === null ? Number(item.current_amount || 0) : null
          )),
        },
        {
          name: `${currentYear} (projeção)`,
          data: (this.projection.by_month || []).map((item) => (
            item.projected_amount !== null ? Number(item.projected_amount || 0) : null
          )),
        },
        {
          name: String(compareYear),
          data: (this.projection.by_month || []).map((item) => Number(item.compare_amount || 0)),
        },
      ];
    },
    chartOptions() {
      return {
        chart: {
          toolbar: { show: false },
          stacked: false,
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: '55%',
          },
        },
        dataLabels: { enabled: false },
        stroke: {
          width: [0, 0, 0],
        },
        xaxis: {
          categories: (this.projection.by_month || []).map((item) => item.label),
        },
        yaxis: {
          labels: {
            formatter: (value) => formatCurrency(value),
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (value) => (value === null ? '-' : formatCurrency(value)),
          },
        },
        colors: ['#292f4c', '#fe715f', '#9e9e9e'],
        legend: { position: 'top' },
      };
    },
  },
  watch: {
    'projection.compare_year'(value) {
      this.localCompareYear = value;
    },
  },
  methods: {
    formatCurrency,
    formatVariation(value) {
      if (value === null || value === undefined) {
        return '-';
      }

      const prefix = value > 0 ? '+' : '';
      return `${prefix}${value}%`;
    },
    variationClass(value) {
      if (value === null || value === undefined) {
        return '';
      }

      if (value > 0) {
        return 'success--text';
      }

      if (value < 0) {
        return 'error--text';
      }

      return '';
    },
  },
};
</script>

<style scoped>
.projection-metric {
  padding: 12px 0;
}

.dashboard-compare-select {
  max-width: 160px;
}
</style>
